<script setup>
import { useVuelidate } from '@vuelidate/core';
import { computed } from 'vue';
import { useContactFormStore } from '@stores/useContactFormStore';
import { PhoneNumber } from '@tools/phoneNumber';
import { UserAuth } from '@tools/user/auth';
import { trans } from '@elements/translation';
import PhoneInput from '@components/PhoneInput.vue';
import Spinner from '@components/Spinner.vue';
import FieldErrorMessage from './FieldErrorMessage.vue';
import { getContactValidator } from './validators/contactValidator';

const props = defineProps({
  validationRules: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  withMessage: {
    type: Boolean,
    default: false,
  },
  messageFieldLabel: {
    type: String,
    default: trans('contact_form.field.message.label.default'),
  },
  submitLabel: {
    type: String,
    default: trans('contact_form.button.submit'),
  },
});

const emit = defineEmits([ 'submit' ]);

const user = computed(() => UserAuth.getUser());

const contactForm = useContactFormStore();
contactForm.load(user.value);

const form = contactForm.getData();

const validationRules = computed(() => ({
  ...getContactValidator(),
  ...props.validationRules,
}));

const showMandatory = (ruleAttribute) => (ruleAttribute.required ? '*' : '');

const validate = useVuelidate(validationRules, form);

const phoneNumber = computed({
  get: () => new PhoneNumber(form.phoneNumber ?? ''),
  set: (val) => {
    form.phoneNumber = val.international;
  },
});

const handleSubmit = () => {
  contactForm.persist();
  emit('submit', { form });
};
</script>

<template>
  <form class="contact-form">
    <div class="contact-form__name">
      <div class="contact-form__field">
        <label
          for="firstname"
          class="contact-form__label"
        >
          {{ trans('contact_form.field.firstname.label') }} {{ showMandatory(validationRules.firstname) }}
        </label>

        <input
          v-model="form.firstname"
          type="text"
          class="contact-form__input"
          :class="{ error: validate.firstname.$error }"
          name="firstname"
          :placeholder="trans('contact_form.field.firstname.placeholder')"
          @blur="validate.firstname.$touch()"
        >

        <FieldErrorMessage
          v-if="validate.firstname.$error"
          :errors="validate.firstname.$errors"
        />
      </div>

      <div class="contact-form__field">
        <label
          for="lastname"
          class="contact-form__label"
        >
          {{ trans('contact_form.field.lastname.label') }} {{ showMandatory(validationRules.lastname) }}
        </label>

        <input
          v-model="form.lastname"
          type="text"
          class="contact-form__input"
          :class="{ error: validate.lastname.$error }"
          name="lastname"
          :placeholder="trans('contact_form.field.lastname.placeholder')"
          @blur="validate.lastname.$touch()"
        >

        <FieldErrorMessage
          v-if="validate.lastname.$error"
          :errors="validate.lastname.$errors"
        />
      </div>
    </div>

    <div class="contact-form__field">
      <label
        for="email"
        class="contact-form__label"
      >
        {{ trans('contact_form.field.email.label') }} {{ showMandatory(validationRules.email) }}
      </label>

      <input
        v-model="form.email"
        type="email"
        class="contact-form__input"
        :class="{ error: validate.email.$error }"
        name="email"
        :disabled="user"
        :placeholder="trans('contact_form.field.email.placeholder')"
        @blur="validate.email.$touch()"
      >

      <FieldErrorMessage
        v-if="validate.email.$error"
        :errors="validate.email.$errors"
      />
    </div>

    <div class="contact-form__field">
      <label
        for="phoneNumber"
        class="contact-form__label"
      >
        {{ trans('contact_form.field.phonenumber.label') }} {{ showMandatory(validationRules.phoneNumber) }}
      </label>

      <PhoneInput
        v-model="phoneNumber"
        :class="{ error: validate.phoneNumber.$error }"
        @blur="validate.phoneNumber.$touch"
      />

      <FieldErrorMessage
        v-if="validate.phoneNumber.$error"
        :errors="validate.phoneNumber.$errors"
      />
    </div>

    <div
      v-if="withMessage"
      class="contact-form__field"
    >
      <label
        for="message"
        class="contact-form__label"
      >
        {{ messageFieldLabel }}
      </label>

      <textarea
        v-model="form.message"
        class="contact-form__input contact-form__input--textarea"
        name="message"
        :placeholder="trans('contact_form.field.message.placeholder')"
      />
    </div>

    <button
      class="btn btn--primary contact-form__submit"
      :disabled="validate.$silentErrors.length || isLoading"
      @click.prevent="handleSubmit"
    >
      <Spinner v-if="isLoading" :size="30" />

      <template v-else>
        <span>
          {{ submitLabel }}
        </span>

        <span
          class="contact-form__submit__icon"
          :class="{ disabled: validate.$silentErrors.length }"
        />
      </template>
    </button>

    <p class="contact-form__note">
      {{ trans('contact_form.note') }}
    </p>
  </form>
</template>
